import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { RewardPopup } from "@/components/RewardPopup";
import { TrendingUp, Coins, LineChart, Percent, Mail, Send, Loader2, CheckCircle2, CalendarCheck, Check, UserRound } from "lucide-react";

const IO_RATE = 0.065;      // interest-only rate on both the 80% loan and 20% equity draw
const MGMT_COST = 0.07;     // property management cost as % of gross rent
const DEP_RATE = 0.012;     // annual depreciation as % of value (new build)

// Australian resident marginal rates (2024–25) incl. 2% Medicare levy.
const TAX_BRACKETS = [
  { label: "Under $18,200", rate: 0 },
  { label: "$18,201 – $45,000", rate: 18 },
  { label: "$45,001 – $135,000", rate: 32 },
  { label: "$135,001 – $190,000", rate: 39 },
  { label: "Over $190,000", rate: 47 },
];

function analyse(value, rentPw, marginal) {
  const grossRent = (rentPw || 0) * 52;
  const mgmt = grossRent * MGMT_COST;
  const interest = (value || 0) * IO_RATE;            // 80% loan + 20% equity drawn = fully financed
  const depreciation = (value || 0) * DEP_RATE;
  const grossYield = value ? (grossRent / value) * 100 : 0;
  const netYield = value ? ((grossRent - mgmt) / value) * 100 : 0;
  const preTaxCashYr = grossRent - mgmt - interest;
  const taxableIncome = grossRent - mgmt - interest - depreciation;
  const taxEffectYr = -taxableIncome * marginal;      // loss → positive (refund)
  const afterTaxCashYr = preTaxCashYr + taxEffectYr;
  return {
    grossRent, opCosts: mgmt, interest, depreciation, grossYield, netYield,
    preTaxWeekly: preTaxCashYr / 52, afterTaxWeekly: afterTaxCashYr / 52,
    taxBenefitYr: taxEffectYr,
  };
}

const Stat = ({ icon: Icon, label, value, sub, tone = "text-brand", testId }) => (
  <div className="rounded-2xl border border-line bg-white/[0.03] p-5" data-testid={testId}>
    <div className={`flex items-center gap-2 ${tone}`}><Icon size={18} /><span className="label-eyebrow text-forest/70">{label}</span></div>
    <div className="font-serif text-2xl text-forest mt-2">{value}</div>
    {sub && <div className="text-xs text-mutedText mt-1">{sub}</div>}
  </div>
);

const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INVESTOR_OFFER_2 = { name: "First-Year Landlord Insurance", value: 1200 };
const BROKERS = ["Matthew Chen", "Ivano Rossi", "Amy Watson"];
const BROKER_TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM"];

export default function InvestorReveal() {
  const { session, data, back, mergeData, addReward } = useJourney();
  const loc = data.investor_location || {};
  const fund = data.investor_funding || {};
  const [marginal, setMarginal] = useState(32);
  const [buyers, setBuyers] = useState(
    data.profile?.buyers && data.profile.buyers.length === 2
      ? data.profile.buyers
      : [
          { name: session?.client_name || "", mobile: "", email: "", contact: "Email" },
          { name: "", mobile: "", email: "", contact: "Email" },
        ]
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!data.journey_sent);
  const [booked, setBooked] = useState(!!(data.broker_appointment && data.broker_appointment.status));
  const [showReward2, setShowReward2] = useState(false);
  const bAppt0 = data.broker_appointment || {};
  const [broker, setBroker] = useState(bAppt0.broker || BROKERS[0]);
  const [brokerDate, setBrokerDate] = useState(bAppt0.date || "");
  const [brokerTime, setBrokerTime] = useState(bAppt0.time || BROKER_TIMES[1]);

  const [regions, setRegions] = useState([]);
  useEffect(() => { api.investorRegions().then((d) => setRegions(d.regions || [])).catch(() => {}); }, []);

  const value = fund.property_value || loc.median || 700000;
  const rent = loc.rent_pw || Math.round((value * 0.045) / 52);
  const loan = Math.round(value * 0.8);       // 80% interest-only investment loan
  const equity20 = Math.round(value * 0.2);   // 20% deposit drawn from home equity
  const deposit = fund.deposit ?? equity20;
  const m = analyse(value, rent, marginal / 100);

  // Three matched properties from the chosen corridor's suburbs.
  const region = regions.find((r) => r.id === loc.region_id);
  const subs = (region?.suburbs || []).filter((s) => s.median && s.rent_pw);
  const score = (s) => (s.gross_yield || 0) + 0.3 * (s.price_growth_12m || 0);
  const bestOverall = subs.slice().sort((a, b) => score(b) - score(a))[0];
  const bestCash = subs.slice().sort((a, b) => (b.gross_yield || 0) - (a.gross_yield || 0))[0];
  const bestGrowth = subs.slice().sort((a, b) => (b.price_growth_12m || 0) - (a.price_growth_12m || 0))[0];

  const recWeekly = (s) => analyse(s.median, s.rent_pw, marginal / 100).afterTaxWeekly;
  const RECS = [
    { tag: "Best overall", icon: TrendingUp, s: bestOverall },
    { tag: "Strongest cash flow", icon: Coins, s: bestCash },
    { tag: "Strongest growth", icon: LineChart, s: bestGrowth },
  ].filter((r) => r.s);

  const surplus = m.afterTaxWeekly >= 0;

  const validEmails = buyers.filter((b) => emailRe.test((b.email || "").trim()));
  const updateBuyer = (i, next) => setBuyers((b) => b.map((x, idx) => (idx === i ? next : x)));

  const saveAndSend = async () => {
    if (validEmails.length === 0) { toast.error("Add at least one valid investor email to send the summary."); return; }
    setSending(true);
    try {
      const revealPayload = {
        suburb: loc.suburb || (bestOverall && bestOverall.name) || "",
        value, rent, loan, deposit,
        gross_yield: Number(m.grossYield.toFixed(1)),
        net_yield: Number(m.netYield.toFixed(1)),
        after_tax_weekly: Math.round(m.afterTaxWeekly),
        pre_tax_weekly: Math.round(m.preTaxWeekly),
        tax_benefit_yr: Math.round(Math.max(0, m.taxBenefitYr)),
        depreciation: Math.round(m.depreciation),
        marginal,
        recommendations: RECS.map((r) => ({
          tag: r.tag, name: r.s.name, median: r.s.median, rent_pw: r.s.rent_pw,
          gross_yield: r.s.gross_yield, price_growth_12m: r.s.price_growth_12m,
          after_tax_weekly: Math.round(recWeekly(r.s)),
        })),
      };
      await api.patchSession(session.id, { data: { profile: { buyers }, investor_reveal: revealPayload } });
      await api.sendJourney(session.id);
      setSent(true);
      toast.success(`Investment summary sent to ${validEmails.length} ${validEmails.length === 1 ? "inbox" : "inboxes"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the summary.");
    } finally {
      setSending(false);
    }
  };

  const bookBroker = () => {
    if (!brokerDate) { toast.error("Choose an assessment date."); return; }
    const appt = { broker, date: brokerDate, time: brokerTime, status: "locked" };
    mergeData({ broker_appointment: appt });
    if (session) api.patchSession(session.id, { data: { broker_appointment: appt } }).catch(() => {});
    setBooked(true);
    if (!(data.rewards || []).some((r) => r.name === INVESTOR_OFFER_2.name)) {
      addReward(INVESTOR_OFFER_2);
      setShowReward2(true);
    } else {
      toast.success("Finance assessment booked.");
    }
  };

  return (
    <StageFrame testId="stage-investor-reveal" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Your investment numbers</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
          {loc.suburb || "Your"} investment — the real numbers.
        </h1>
        <p className="text-mutedText mt-3 max-w-2xl">
          {money(value)} property (house + land) · {money(rent)}/wk rent · {money(loan)} loan (80%) + {money(equity20)} equity (20%), interest-only at {(IO_RATE * 100).toFixed(1)}%.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat icon={Percent} label="Gross yield" value={`${m.grossYield.toFixed(1)}%`} sub={`${money(m.grossRent)} rent / yr`} testId="reveal-gross-yield" />
          <Stat icon={Percent} label="Net yield" value={`${m.netYield.toFixed(1)}%`} sub={`after ${money(m.opCosts)} costs`} testId="reveal-net-yield" />
          <Stat icon={Coins} label={surplus ? "Weekly surplus" : "Weekly holding cost"} value={`${surplus ? "+" : "−"}${money(Math.abs(m.afterTaxWeekly))}/wk`} sub="after tax" tone={surplus ? "text-emerald-300" : "text-brand"} testId="reveal-weekly" />
          <Stat icon={TrendingUp} label="Est. tax benefit" value={`${money(Math.max(0, m.taxBenefitYr))}/yr`} sub={`incl. ${money(m.depreciation)} depreciation`} testId="reveal-tax-benefit" />
        </div>

        <div className="mt-6 rounded-2xl border border-line bg-white/5 p-5" data-testid="reveal-tax-brackets">
          <div className="flex items-center justify-between text-sm">
            <span className="text-forest/80">Your taxable income (sets your marginal rate)</span>
            <span className="font-medium text-brand">{marginal}%</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {TAX_BRACKETS.map((b) => (
              <button key={b.rate} data-testid={`tax-bracket-${b.rate}`} onClick={() => setMarginal(b.rate)}
                className={`rounded-full border px-3.5 py-2 text-xs font-medium transition ${marginal === b.rate ? "border-brand bg-brand text-white" : "border-line text-forest/75 hover:border-brand/50"}`}>
                {b.label} · {b.rate}%
              </button>
            ))}
          </div>
          <div className="mt-3 text-xs text-mutedText">
            Pre-tax cash flow {m.preTaxWeekly >= 0 ? "+" : "−"}{money(Math.abs(m.preTaxWeekly))}/wk · negative gearing turns it into {surplus ? "a surplus" : `just ${money(Math.abs(m.afterTaxWeekly))}/wk`} after tax.
          </div>
        </div>

        {RECS.length > 0 && (
          <div className="mt-12" data-testid="reveal-recommendations">
            <h2 className="font-serif text-2xl text-forest">Three properties that fit your goal</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {RECS.map((r, i) => {
                const wk = recWeekly(r.s);
                const pos = wk >= 0;
                return (
                  <motion.div key={r.tag} data-testid={`reveal-rec-${i}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                    className="rounded-2xl border border-line bg-white/[0.03] p-5">
                    <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-brand/15 text-brand"><r.icon size={20} /></div>
                    <span className="label-eyebrow text-brand mt-4 block">{r.tag}</span>
                    <h3 className="font-serif text-xl text-forest mt-1">{r.s.name}</h3>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-mutedText">Median</span><span className="text-forest/90">{money(r.s.median)}</span></div>
                      <div className="flex justify-between"><span className="text-mutedText">Rent</span><span className="text-forest/90">${r.s.rent_pw}/wk</span></div>
                      <div className="flex justify-between"><span className="text-mutedText">Gross yield</span><span className="text-emerald-300">{r.s.gross_yield}%</span></div>
                      <div className="flex justify-between"><span className="text-mutedText">12m growth</span><span className="text-brand">{r.s.price_growth_12m ?? "—"}%</span></div>
                      <div className="flex justify-between border-t border-line pt-2"><span className="text-forest">After-tax</span><span className={pos ? "text-emerald-300 font-medium" : "text-forest font-medium"}>{pos ? "+" : "−"}{money(Math.abs(wk))}/wk</span></div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-3xl border border-line bg-white/[0.03] p-6 md:p-8" data-testid="investor-broker-card">
          <div className="flex items-center gap-2 text-brand"><CalendarCheck size={18} /><span className="label-eyebrow text-forest/70">Next step · Finance assessment</span></div>
          <h2 className="font-serif text-2xl text-forest mt-2">Book your finance assessment with the Broker.</h2>
          <p className="text-mutedText text-sm mt-1.5">Lock in your borrowing position and structure — and unlock a second free offer.</p>

          {!booked ? (
            <div className="mt-6 grid gap-5">
              <div>
                <label className="label-eyebrow text-mutedText">Broker</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {BROKERS.map((c) => (
                    <button key={c} data-testid={`investor-broker-consultant-${c.split(" ")[0]}`} onClick={() => setBroker(c)}
                      className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${broker === c ? "border-brand bg-brand/15 text-forest" : "border-line text-mutedText"}`}>
                      <UserRound size={14} /> {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="label-eyebrow text-mutedText">Date</label>
                  <input data-testid="investor-broker-date" type="date" value={brokerDate} onChange={(e) => setBrokerDate(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-line bg-white/5 px-4 py-3 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
                <div>
                  <label className="label-eyebrow text-mutedText">Time</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {BROKER_TIMES.map((t) => (
                      <button key={t} data-testid={`investor-broker-time-${t.replace(/[: ]/g, "")}`} onClick={() => setBrokerTime(t)}
                        className={`rounded-full border px-3 py-2 text-sm transition ${brokerTime === t ? "border-brand bg-brand/15 text-forest" : "border-line text-mutedText"}`}>{t}</button>
                    ))}
                  </div>
                </div>
              </div>
              <button data-testid="investor-book-broker-btn" onClick={bookBroker}
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-white text-sm font-medium hover:bg-brand-dark transition">
                <CalendarCheck size={16} /> Confirm assessment
              </button>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
              data-testid="investor-broker-locked" className="mt-6 rounded-2xl border border-brand/40 bg-brand/10 p-6">
              <div className="flex items-center gap-2 text-brand"><Check size={18} /><span className="font-medium">Finance Assessment — Locked In</span></div>
              <p className="mt-3 text-forest text-lg">{broker}</p>
              <p className="text-mutedText">{brokerDate} · {brokerTime}</p>
            </motion.div>
          )}
        </div>

        <div className="mt-6 rounded-3xl border border-line bg-white/[0.03] p-6 md:p-8" data-testid="investor-profile-capture">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-brand" />
            <h2 className="font-serif text-2xl text-forest">Send this investment plan</h2>
          </div>
          <p className="text-mutedText text-sm mt-1.5">We'll email a branded summary — the numbers, yields and matched properties — for you to revisit any time.</p>
          <div className="mt-6 space-y-4">
            {buyers.map((b, i) => {
              const p = `investor-profile-buyer${i + 1}`;
              const set = (k, v) => updateBuyer(i, { ...b, [k]: v });
              return (
                <div key={i} className="rounded-2xl border border-line bg-white/5 p-5">
                  <span className="label-eyebrow text-mutedText">{i === 0 ? "Investor" : "Co-investor (optional)"}</span>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid={`${p}-name`} value={b.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
                    <input data-testid={`${p}-mobile`} value={b.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Mobile" inputMode="tel" className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
                    <input data-testid={`${p}-email`} value={b.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" inputMode="email" className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button data-testid="investor-send-btn" onClick={saveAndSend} disabled={sending}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-white text-sm font-medium shadow-xl shadow-brand/25 transition hover:bg-brand-dark disabled:opacity-50">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : sent ? <><Send size={16} /> Resend summary</> : <><Send size={16} /> Save &amp; email summary</>}
            </button>
            {sent && <span data-testid="investor-sent-status" className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} /> Sent to {validEmails.length} {validEmails.length === 1 ? "inbox" : "inboxes"}</span>}
          </div>
        </div>

        <p className="mt-8 text-xs text-mutedText/70 max-w-3xl">
          Indicative only. Repayments assume the full price is financed — an 80% interest-only investment loan plus the 20% deposit drawn from home equity — at {(IO_RATE * 100).toFixed(1)}%, management {Math.round(MGMT_COST * 100)}% of gross rent,
          depreciation {(DEP_RATE * 100).toFixed(1)}% of value, and a marginal rate from ATO tax thresholds. Not financial or tax advice — figures do not account for capital gains tax;
          seek advice from your accountant before relying on negative gearing.
        </p>
      </div>

      <RewardPopup open={showReward2} onClose={() => setShowReward2(false)} title={INVESTOR_OFFER_2.name} value={INVESTOR_OFFER_2.value} subtitle="Locked in when you book your finance assessment." />
      <ConsultantNav testId="stage-investor-reveal" onBack={back} onNext={null} />
    </StageFrame>
  );
}
